import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSubscriptions } from "../../hooks/Planpage/useSubscriptions";
import { usePromoCode } from "../../hooks/Planpage/usePromoCode";
import WorkoutPlanCard from "./SubscriptionPlanCard";
import DietPlanCard from "./AddonPlanCard";
import PromoCodeModal from "./PromoCodeModal";
import { fetchUserSubscription } from "../../api/subscription"; // Import the API function

const HorizontalScrollSection = ({ title, children }) => {
  const scrollContainerRef = React.useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 20);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 20);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
      setTimeout(checkScrollPosition, 300);
    }
  };

  React.useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", checkScrollPosition);
      return () => {
        container.removeEventListener("scroll", checkScrollPosition);
        window.removeEventListener("resize", checkScrollPosition);
      };
    }
  }, []);

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-6 px-2">
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        <div className="flex gap-2">
          {showLeftArrow && (
            <button
              onClick={scrollLeft}
              className="p-2 bg-white border border-gray-200 rounded-full hover:bg-lime-500 hover:text-white hover:border-lime-500 transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {showRightArrow && (
            <button
              onClick={scrollRight}
              className="p-2 bg-white border border-gray-200 rounded-full hover:bg-lime-500 hover:text-white hover:border-lime-500 transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto gap-5 pb-4 scroll-smooth"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
            paddingLeft: "4px",
            paddingRight: "16px",
            paddingTop: "4px",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-lime-500 border-t-transparent"></div>
      <p className="mt-4 text-gray-600">Loading plans...</p>
    </div>
  </div>
);

const ErrorDisplay = ({ error, onRetry }) => (
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center min-h-screen">
    <div className="text-center">
      <p className="text-gray-800 font-semibold">Error loading plans</p>
      <p className="text-gray-600 text-sm mt-2">{error}</p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-lime-500 text-black rounded-lg hover:bg-lime-600 transition"
      >
        Retry
      </button>
    </div>
  </div>
);

const PlanPage = () => {
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [selectedDiet, setSelectedDiet] = useState(null);
  const navigate = useNavigate();
  const { workoutPlans, dietPlans, loading, error } = useSubscriptions();
  const {
    promoCode,
    setPromoCode,
    promoApplied,
    showPromoModal,
    setShowPromoModal,
    applyPromo,
  } = usePromoCode();

  // ── Read purchased subscription from API and localStorage ──────────────
  const [purchasedWorkoutId, setPurchasedWorkoutId] = useState(null);
  const [purchasedAddonIds, setPurchasedAddonIds] = useState([]);
  const [hasBothPlanAndAddons, setHasBothPlanAndAddons] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        // First try to fetch from API
        const subscription = await fetchUserSubscription();
        
        if (subscription && subscription.status === "Active") {
          setSubscriptionData(subscription);
          
          // Set workout plan
          if (subscription.plan?.id) {
            setPurchasedWorkoutId(String(subscription.plan.id));
          }
          
          // Set addon ids
          if (Array.isArray(subscription.addons) && subscription.addons.length > 0) {
            const addonIds = subscription.addons.map((a) => String(a.id || a._id || ""));
            setPurchasedAddonIds(addonIds);
          }
          
          // Check if user has both plan and addons
          const hasPlan = !!subscription.plan?.id;
          const hasAddons = subscription.addons && subscription.addons.length > 0;
          setHasBothPlanAndAddons(hasPlan && hasAddons);
        } else {
          // Fallback to localStorage if API returns null/inactive
          const raw = localStorage.getItem("userSubscription");
          if (raw) {
            const userSubscription = JSON.parse(raw);
            if (userSubscription?.status === "Active") {
              if (userSubscription?.plan?.id) {
                setPurchasedWorkoutId(String(userSubscription.plan.id));
              }
              if (Array.isArray(userSubscription?.addons) && userSubscription.addons.length > 0) {
                const addonIds = userSubscription.addons.map((a) => String(a.id || a._id || ""));
                setPurchasedAddonIds(addonIds);
              }
              const hasPlan = !!userSubscription?.plan?.id;
              const hasAddons = userSubscription?.addons?.length > 0;
              setHasBothPlanAndAddons(hasPlan && hasAddons);
            }
          }
        }
      } catch (e) {
        console.error("Error loading subscription:", e);
        // Fallback to localStorage on error
        try {
          const raw = localStorage.getItem("userSubscription");
          if (raw) {
            const userSubscription = JSON.parse(raw);
            if (userSubscription?.status === "Active") {
              if (userSubscription?.plan?.id) {
                setPurchasedWorkoutId(String(userSubscription.plan.id));
              }
              if (Array.isArray(userSubscription?.addons) && userSubscription.addons.length > 0) {
                const addonIds = userSubscription.addons.map((a) => String(a.id || a._id || ""));
                setPurchasedAddonIds(addonIds);
              }
              const hasPlan = !!userSubscription?.plan?.id;
              const hasAddons = userSubscription?.addons?.length > 0;
              setHasBothPlanAndAddons(hasPlan && hasAddons);
            }
          }
        } catch (err) {
          // ignore
        }
      }
    };
    
    loadSubscription();
  }, []);
  // ──────────────────────────────────────────────────────────────

  const handleBack = () => navigate(-1);

  const calculateTotal = () => {
    let total = 0;
    if (selectedWorkout) total += parseInt(selectedWorkout.discountedPrice) || 0;
    if (selectedDiet) total += parseInt(selectedDiet.discountedPrice) || 0;
    return total;
  };

  const getButtonText = () => {
    // If workout already purchased, only diet can be bought additionally
    if (purchasedWorkoutId) {
      if (selectedDiet && !purchasedAddonIds.includes(String(selectedDiet.id))) {
        return `Pay | Rs. ${calculateTotal()}`;
      }
      return null; // nothing new selected to buy
    }
    // Normal flow (no purchased workout)
    if (!selectedWorkout && !selectedDiet) return null;
    if (selectedDiet && !selectedWorkout) return "Select a Plan";
    if (selectedWorkout) return `Pay | Rs. ${calculateTotal()}`;
    return null;
  };

  const handleCheckout = () => {
    // If workout already purchased, allow checkout for diet only
    if (purchasedWorkoutId) {
      if (!selectedDiet) {
        alert("Please select a diet plan to continue!");
        return;
      }
      navigate("/checkout", {
        state: { selectedWorkout: null, selectedDiet, total: calculateTotal() },
      });
      return;
    }
    // Normal flow
    if (!selectedWorkout) {
      alert("Please select a workout plan first!");
      return;
    }
    navigate("/checkout", {
      state: { selectedWorkout, selectedDiet, total: calculateTotal() },
    });
  };

  const buttonText = getButtonText();
  // Show promo code button only if user does NOT have both plan AND addons
  const showPromoButton = !hasBothPlanAndAddons;

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorDisplay error={error} onRetry={() => window.location.reload()} />;

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-4 px-6 font-sans pb-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 text-center flex-1">
          Explore Plans
        </h1>
        <div className="w-10"></div>
      </div>

      {/* Promo Code Button - conditionally rendered */}
      {showPromoButton && (
  <button
    onClick={() => setShowPromoModal(true)}
    className="px-6 py-2.5 bg-lime-500 hover:bg-lime-600 text-black text-base font-medium rounded-full transition duration-300 mb-6 w-fit mx-auto block"
  >
    {promoApplied ? "✓ Promocode Applied" : "Use Promocode"}
  </button>
)}

      {workoutPlans.length > 0 && (
        <HorizontalScrollSection title="Workout Plans">
          {workoutPlans
            .filter((plan) => !purchasedWorkoutId || purchasedWorkoutId === String(plan.id))
            .map((plan) => {
              const isPurchased = purchasedWorkoutId === String(plan.id);
              const activeSubscription = subscriptionData && subscriptionData.status === "Active" && subscriptionData.plan?.id === String(plan.id);
              
              return (
                <WorkoutPlanCard
                  key={plan.id}
                  {...plan}
                  isSelected={isPurchased || selectedWorkout?.id === plan.id}
                  isPurchased={isPurchased}
                  activeSubscription={activeSubscription}
                  expiryDate={activeSubscription ? subscriptionData.expiryDate : null}
                  onSelect={() => {
                    if (isPurchased) return;
                    setSelectedWorkout(selectedWorkout?.id === plan.id ? null : plan);
                  }}
                />
              );
            })}
        </HorizontalScrollSection>
      )}

      {dietPlans.length > 0 && (
        <HorizontalScrollSection title="Diet & Nutrition">
          {dietPlans
            .filter((plan) => {
              const isPurchased = purchasedAddonIds.includes(String(plan.id));
              if (purchasedWorkoutId && purchasedAddonIds.length > 0) return isPurchased;
              return true;
            })
            .map((plan) => {
              const isPurchased = purchasedAddonIds.includes(String(plan.id));
              const activeAddon = subscriptionData?.addons?.find(
                (addon) => String(addon.id) === String(plan.id)
              );
              
              return (
                <DietPlanCard
                  key={plan.id}
                  {...plan}
                  isSelected={isPurchased || selectedDiet?.id === plan.id}
                  isPurchased={isPurchased}
                  activeSubscription={!!activeAddon}
                  expiryDate={activeAddon?.expiryDate || (isPurchased ? subscriptionData?.expiryDate : null)}
                  onSelect={() => {
                    if (isPurchased) return;
                    setSelectedDiet(selectedDiet?.id === plan.id ? null : plan);
                  }}
                />
              );
            })}
        </HorizontalScrollSection>
      )}

      {/* Show Pay button when there is something new to purchase */}
      {buttonText && (
        <div className="flex justify-center mt-8 mb-8">
          <button
            onClick={handleCheckout}
            disabled={buttonText === "Select a Plan"}
            className={`bg-lime-500 text-black font-medium text-base px-12 py-4 rounded-full transition-all duration-300 transform active:scale-95 min-w-[250px] ${
              buttonText === "Select a Plan"
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-lime-600"
            }`}
          >
            {buttonText}
          </button>
        </div>
      )}

      {/* Promo Code Button - conditionally rendered at bottom as well */}
      {showPromoButton && (
        <button
          onClick={() => setShowPromoModal(true)}
          className="px-6 py-2.5 bg-lime-500 hover:bg-lime-600 text-black text-base font-medium rounded-full transition duration-300 mb-6 w-fit mx-auto block">
          {promoApplied ? "✓ Promocode Applied" : "Use Promocode"}
        </button>
      )}

      <PromoCodeModal
        isOpen={showPromoModal}
        onClose={() => setShowPromoModal(false)}
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        onApply={applyPromo}
        promoApplied={promoApplied}
      />
    </div>
  );
};

export default PlanPage;